import {
  type ReactNode,
  type RefObject,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { Icone, type NomeIcone } from './Icone';

/**
 * Camadas abertas (menus, popovers, modais), na ordem em que abriram: só a
 * de cima responde ao Esc e ao clique fora — senão um Esc fecharia todas, e
 * clicar num seletor aberto por um modal fecharia o modal.
 */
const pilhaDeCamadas: number[] = [];
let proximaCamada = 1;

/** Fecha com clique fora ou Esc (menus, popovers, seletor de emoji, modais). */
export function useFecharFora(
  aberto: boolean,
  fechar: () => void,
  ...refs: Array<RefObject<HTMLElement | null>>
) {
  const fecharRef = useRef(fechar);
  useEffect(() => {
    fecharRef.current = fechar;
  });
  useEffect(() => {
    if (!aberto) return;
    const id = proximaCamada++;
    pilhaDeCamadas.push(id);
    const noTopo = () => pilhaDeCamadas[pilhaDeCamadas.length - 1] === id;
    const clique = (evento: PointerEvent) => {
      if (!noTopo()) return;
      const alvo = evento.target as Node;
      if (refs.some((ref) => ref.current?.contains(alvo))) return;
      fecharRef.current();
    };
    const tecla = (evento: KeyboardEvent) => {
      if (evento.key !== 'Escape' || !noTopo()) return;
      evento.stopPropagation();
      fecharRef.current();
    };
    document.addEventListener('pointerdown', clique, true);
    document.addEventListener('keydown', tecla, true);
    return () => {
      const indice = pilhaDeCamadas.indexOf(id);
      if (indice >= 0) pilhaDeCamadas.splice(indice, 1);
      document.removeEventListener('pointerdown', clique, true);
      document.removeEventListener('keydown', tecla, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);
}

export type Posicao = { x: number; y: number };

/**
 * Camada flutuante presa a um ponto da tela (menu, popover): fica dentro da
 * janela, abrindo para cima ou para a esquerda quando não cabe.
 */
export function Flutuante({
  ancora,
  aoFechar,
  children,
  rotulo,
  papel = 'dialog',
  alinhar = 'inicio',
  abrir = 'baixo',
}: {
  ancora: Posicao;
  aoFechar: () => void;
  children: ReactNode;
  rotulo: string;
  papel?: 'dialog' | 'menu';
  /** `fim`: o canto direito da camada fica no ponto. */
  alinhar?: 'inicio' | 'fim';
  /** `cima`: a camada termina no ponto (menu do rodapé, seletor do compositor). */
  abrir?: 'baixo' | 'cima';
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [posicao, setPosicao] = useState<Posicao | null>(null);
  useFecharFora(true, aoFechar, ref);
  useLayoutEffect(() => {
    const caixa = ref.current?.getBoundingClientRect();
    if (!caixa) return;
    const margem = 8;
    let x = alinhar === 'fim' ? ancora.x - caixa.width : ancora.x;
    let y = abrir === 'cima' ? ancora.y - caixa.height : ancora.y;
    if (x + caixa.width > window.innerWidth - margem) {
      x = window.innerWidth - margem - caixa.width;
    }
    if (y + caixa.height > window.innerHeight - margem) {
      y = Math.max(margem, ancora.y - caixa.height);
    }
    setPosicao({ x: Math.max(margem, x), y: Math.max(margem, y) });
  }, [ancora.x, ancora.y, alinhar, abrir]);
  return createPortal(
    <div
      ref={ref}
      role={papel}
      aria-label={rotulo}
      className="fixed z-50"
      style={{
        left: posicao?.x ?? ancora.x,
        top: posicao?.y ?? ancora.y,
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

/** Caixa de menu do design (C6): fundo lateral, borda, raio 10, sombra. */
export function CaixaMenu({
  children,
  largura = 220,
}: {
  children: ReactNode;
  largura?: number;
}) {
  return (
    <div
      className="flex flex-col rounded-[10px] border border-borda bg-lateral p-1.5 shadow-[0_12px_32px_rgba(0,0,0,.45)]"
      style={{ width: largura }}
    >
      {children}
    </div>
  );
}

export function ItemMenu({
  children,
  aoEscolher,
  perigo = false,
  icone,
  atalho,
  marcado,
  descricao,
}: {
  children: ReactNode;
  aoEscolher: (evento: React.MouseEvent<HTMLButtonElement>) => void;
  perigo?: boolean;
  icone?: NomeIcone | ReactNode;
  atalho?: string;
  /** Item de escolha única (status): marcado ou não. */
  marcado?: boolean;
  descricao?: string;
}) {
  return (
    <button
      type="button"
      role={marcado === undefined ? 'menuitem' : 'menuitemradio'}
      aria-checked={marcado}
      onClick={aoEscolher}
      className={`group flex min-h-8 w-full items-center gap-2 rounded-md px-2 py-1 text-left text-sm font-medium hover:bg-destaque hover:text-destaque-texto focus-visible:bg-destaque focus-visible:text-destaque-texto focus-visible:outline-none ${
        perigo ? 'text-ocupado' : 'text-texto'
      }`}
    >
      {typeof icone === 'string' ? null : icone}
      <span className="flex min-w-0 grow flex-col">
        <span className="truncate">{children}</span>
        {descricao && (
          <span className="text-xs leading-snug text-texto-3 group-hover:text-destaque-texto">
            {descricao}
          </span>
        )}
      </span>
      {atalho && (
        <kbd className="font-sans text-xs text-texto-3 group-hover:text-destaque-texto">
          {atalho}
        </kbd>
      )}
      {typeof icone === 'string' && (
        <Icone nome={icone as NomeIcone} tamanho={18} />
      )}
      {marcado && <Icone nome="check" tamanho={16} />}
    </button>
  );
}

export function SeparadorMenu() {
  return (
    <div role="separator" className="mx-1.5 my-1 h-px shrink-0 bg-borda" />
  );
}

/**
 * Modal do design: véu escuro, cartão elevado, título Bricolage, rodapé com
 * os botões à direita. Esc e clique no véu fecham.
 */
export function Modal({
  titulo,
  descricao,
  children,
  rodape,
  aoFechar,
  largura = 440,
}: {
  titulo: string;
  descricao?: ReactNode;
  children?: ReactNode;
  /** Sem rodapé: o modal só fecha pelo Esc ou clique fora. */
  rodape?: ReactNode;
  aoFechar: () => void;
  largura?: number;
}) {
  const id = useId();
  const cartao = useRef<HTMLDivElement>(null);
  useFecharFora(true, aoFechar, cartao);
  useEffect(() => {
    const anterior = document.activeElement as HTMLElement | null;
    // O campo marcado; sem ele, o primeiro botão.
    (
      cartao.current?.querySelector<HTMLElement>('[data-foco-inicial]') ??
      cartao.current?.querySelector<HTMLElement>('button')
    )?.focus();
    return () => anterior?.focus?.();
  }, []);
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(5,6,8,.72)] p-4">
      <div
        ref={cartao}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-titulo`}
        className="flex max-h-full flex-col overflow-hidden rounded-xl bg-elevado shadow-[0_12px_32px_rgba(0,0,0,.45)]"
        style={{ width: largura }}
      >
        <div className="flex flex-col gap-4 overflow-y-auto p-6">
          <div>
            <h2
              id={`${id}-titulo`}
              className="m-0 font-titulo text-[22px] leading-7 font-bold"
            >
              {titulo}
            </h2>
            {descricao && (
              <p className="mt-2 mb-0 text-[15px] leading-normal text-texto-2">
                {descricao}
              </p>
            )}
          </div>
          {children}
        </div>
        {rodape && (
          <div className="flex items-center justify-end gap-2 bg-rodape p-4">
            {rodape}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

type TipoBotao = 'primario' | 'secundario' | 'perigo' | 'fantasma';

const BOTOES: Record<TipoBotao, string> = {
  primario: 'bg-destaque text-destaque-texto hover:brightness-105',
  secundario: 'bg-ativo text-texto hover:brightness-110',
  perigo: 'bg-perigo text-white hover:brightness-110',
  fantasma: 'bg-transparent text-texto-2 hover:text-texto hover:underline',
};

export function Botao({
  tipo = 'primario',
  children,
  className = '',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { tipo?: TipoBotao }) {
  return (
    <button
      type="button"
      {...props}
      className={`flex h-10 items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50 ${BOTOES[tipo]} ${className}`}
    >
      {children}
    </button>
  );
}
